/**
 * /verify-email, process the email verification.
 *
 * Two modes, auto-selected by URL params:
 *   1. ?token=... → legacy magic-link flow. Auto-POST to /api/verify-email.
 *   2. No token   → render a 6-digit code input. The Neon Auth Better Auth
 *                   instance sends OTP codes (per Ean's screenshot 2026-06-03);
 *                   POST to /api/verify-email-otp.
 *
 * Either path on success → refresh session → /home.
 */

import { AuthLayout, Button } from '@olytic/foundation';
import { type ChangeEvent, type FormEvent, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { authFrame } from '../components/authFrame.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { ApiError, sendVerificationEmail, verifyEmail, verifyEmailOtp } from '../lib/api.ts';
import { RouterNavLink } from '../lib/foundationLinks.tsx';

type Status =
  | 'verifying' // link flow: in-flight
  | 'success' // link or OTP success
  | 'failure' // link failed
  | 'awaiting_code' // no token; render code input
  | 'submitting_code'; // OTP POST in flight

export function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refresh } = useSession();
  const token = searchParams.get('token') ?? '';

  const [status, setStatus] = useState<Status>(token === '' ? 'awaiting_code' : 'verifying');
  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resentBanner, setResentBanner] = useState<string | null>(null);

  // Magic-link auto-verify path.
  useEffect(() => {
    if (token === '') return;
    let cancelled = false;
    (async () => {
      try {
        await verifyEmail(token);
        if (cancelled) return;
        setStatus('success');
        refresh();
        setTimeout(() => {
          if (!cancelled) navigate('/home');
        }, 1500);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) setErrorMessage(err.message);
        else setErrorMessage('Verification failed. Please try again.');
        setStatus('failure');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, refresh, navigate]);

  // If the user isn't signed in AND no token, route to sign-in (they need a
  // session to verify by OTP, Better Auth's OTP-verify endpoint resolves the
  // email from the user's session).
  if (token === '' && user === null) {
    return <Navigate to="/sign-in?next=/verify-email" replace />;
  }

  function handleCodeChange(e: ChangeEvent<HTMLInputElement>) {
    const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(digitsOnly);
    if (errorMessage !== null) setErrorMessage(null);
  }

  async function handleSubmitCode(e: FormEvent) {
    e.preventDefault();
    if (code.length !== 6) {
      setErrorMessage('Please enter the full 6-digit code from your email.');
      return;
    }
    setStatus('submitting_code');
    setErrorMessage(null);
    try {
      await verifyEmailOtp(code);
      setStatus('success');
      refresh();
      setTimeout(() => navigate('/home'), 1500);
    } catch (err) {
      if (err instanceof ApiError) setErrorMessage(err.message);
      else setErrorMessage('Verification failed. Please try again.');
      setStatus('awaiting_code');
    }
  }

  async function handleResend() {
    setResentBanner(null);
    setErrorMessage(null);
    try {
      await sendVerificationEmail();
      setResentBanner('Sent. Check your inbox (and spam) for a fresh 6-digit code.');
    } catch (err) {
      if (err instanceof ApiError) setErrorMessage(err.message);
      else setErrorMessage('Could not send a new code. Try again in a moment.');
    }
  }

  if (status === 'verifying') {
    return (
      <AuthLayout {...authFrame} title="Verifying your email…" description="Just a moment.">
        <div className="loading">
          <span className="spinner" />
        </div>
      </AuthLayout>
    );
  }

  if (status === 'success') {
    return (
      <AuthLayout
        {...authFrame}
        title="Email verified"
        description="Your email is confirmed. Sending you to your workspace…"
      >
        <Button asChild variant="brand" style={{ width: '100%' }}>
          <RouterNavLink href="/home">Go to workspace</RouterNavLink>
        </Button>
      </AuthLayout>
    );
  }

  if (status === 'failure') {
    return (
      <AuthLayout
        {...authFrame}
        title="Verification failed"
        description={errorMessage ?? 'This verification link is invalid or has expired.'}
        footer={
          <>
            Already verified?{' '}
            <Link to="/sign-in" style={{ color: 'var(--olytic-obsidian)', fontWeight: 500 }}>
              Sign in
            </Link>
          </>
        }
      >
        <div className="callout callout-warning" style={{ marginBottom: '16px' }}>
          <p className="callout-body">Sign in and request a fresh code from your home page.</p>
        </div>
        <Button asChild variant="brand" style={{ width: '100%' }}>
          <RouterNavLink href="/sign-in">Sign in</RouterNavLink>
        </Button>
      </AuthLayout>
    );
  }

  // awaiting_code or submitting_code
  return (
    <AuthLayout
      {...authFrame}
      title="Verify your email"
      description={
        user !== null
          ? `Enter the 6-digit code we sent to ${user.email}.`
          : 'Enter the 6-digit code from your verification email.'
      }
      footer={
        <>
          Didn't get it?{' '}
          <Button type="button" variant="link" onClick={handleResend}>
            Resend code
          </Button>
        </>
      }
    >
      {/* Push #84 round 19. softer success card to match ForgotPassword.
         Same shape as the "Check your inbox" treatment so all auth-shell
         success/info states read consistently. */}
      {resentBanner !== null && (
        <p
          style={{
            margin: '0 0 16px 0',
            padding: '12px 16px',
            background: 'var(--olytic-surface)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            color: 'var(--fg-muted)',
            fontSize: 13,
            lineHeight: 1.55,
          }}
          aria-live="polite"
        >
          {resentBanner}
        </p>
      )}
      {errorMessage !== null && (
        <div className="callout callout-warning" style={{ marginBottom: '16px' }}>
          <p className="callout-body">{errorMessage}</p>
        </div>
      )}
      <form onSubmit={handleSubmitCode}>
        <label htmlFor="verify-otp" className="form-label" style={{ display: 'block' }}>
          6-digit code
        </label>
        <input
          id="verify-otp"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="\d{6}"
          maxLength={6}
          value={code}
          onChange={handleCodeChange}
          placeholder="000000"
          aria-label="6-digit verification code"
          className="form-input verify-otp-input"
          autoFocus
        />
        <Button
          type="submit"
          variant="brand"
          disabled={code.length !== 6 || status === 'submitting_code'}
          style={{ width: '100%', marginTop: '12px' }}
        >
          {status === 'submitting_code' ? 'Verifying…' : 'Verify email'}
        </Button>
      </form>
    </AuthLayout>
  );
}
